import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Banner, Button, Card, EmptyState, Field, Mono, PageBody, PageHeader, StackedList } from "../ui/Field";

type Key = { id: string; name: string; scopes: string; last_used_at: number | null; created_at: number };

export default function ApiKeys() {
  const { slug } = useParams();
  const [items, setItems] = useState<Key[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [reveal, setReveal] = useState<{ name: string; secret: string } | null>(null);

  const load = () => api.get<{ items: Key[] }>(`/api/sites/${slug}/api-keys`).then((r) => setItems(r.items)).catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setReveal(null);
    try {
      const r = await api.post<{ id: string; name: string; secret: string }>(`/api/sites/${slug}/api-keys`, { name });
      setReveal({ name: r.name, secret: r.secret });
      setName("");
      load();
    } catch (e) { setErr((e as Error).message); }
  }
  async function remove(id: string) {
    if (!confirm("Revoke this API key?")) return;
    try { await api.delete(`/api/sites/${slug}/api-keys/${id}`); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 11 · Public API"
        title={<>API <em>keys</em></>}
        description={<>Read-only credentials for the public Content API at <Mono>https://api.staticinfo.com/content/{slug}/…</Mono></>}
      />
      {err && <Banner kind="error">{err}</Banner>}
      {reveal && (
        <Banner kind="success" title={`Key '${reveal.name}' created`}>
          <div className="mb-2">Copy the secret now — it cannot be shown again.</div>
          <pre className="bg-[var(--color-surface)] border border-[var(--color-rule)] p-3 rounded-[var(--radius-sm)] text-[12.5px] font-mono break-all text-[var(--color-ink)]">{reveal.secret}</pre>
        </Banner>
      )}

      <Card number="K0" title="New API key">
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Production app" required className="mb-0" />
          <Button type="submit">Create key</Button>
        </form>
      </Card>

      <Card number="K1" title={`Existing keys (${items.length})`} className="mt-6" bodyClassName={items.length === 0 ? "p-5" : "p-0"}>
        {items.length === 0 ? (
          <EmptyState title="No keys yet." description="Create one above to begin consuming the Content API." />
        ) : (
          <StackedList className="px-5">
            {items.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-4 py-3.5">
                <div className="min-w-0">
                  <div className="text-[14px] text-[var(--color-ink)] font-medium">{k.name}</div>
                  <div className="text-[11.5px] text-[var(--color-muted)] font-mono mt-0.5 truncate">
                    scopes: {k.scopes} · created {new Date(k.created_at).toLocaleString()}
                    {k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleString()}` : " · never used"}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(k.id)}>Revoke</Button>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
